import React, { useState, useEffect } from "react";
import { Zap, Copy, Check, ShieldCheck, Mail, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { plans } from "../mock";
import { fetchWallets } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const PurchaseModal = ({ open, onOpenChange, initialPlan }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [plan, setPlan] = useState(initialPlan || plans[1]);
  const [email, setEmail] = useState("");
  const [wallets, setWallets] = useState([]);
  const [coin, setCoin] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loadingWallets, setLoadingWallets] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setPlan(initialPlan || plans[1]);
      setEmail("");
      setCopied(false);
      setLoadingWallets(true);
      fetchWallets()
        .then((data) => {
          setWallets(data);
          setCoin(data[0] || null);
        })
        .catch(() => setWallets([]))
        .finally(() => setLoadingWallets(false));
    }
  }, [open, initialPlan]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const copyAddr = async () => {
    if (!coin) return;
    const text = coin.address;
    let ok = false;

    // Try the modern Clipboard API first (may be blocked by permissions policy
    // when the app runs inside an iframe).
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (e) {
      ok = false;
    }

    // Fallback: temporary textarea + execCommand('copy'), works when the
    // Clipboard API is blocked.
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) {
        ok = false;
      }
    }

    if (ok) {
      setCopied(true);
      toast({ title: "Address copied", description: `${coin.coin} wallet address copied to clipboard.` });
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast({
        title: "Couldn't auto-copy",
        description: "Tap and hold the address above to select and copy it manually.",
        variant: "destructive",
      });
    }
  };

  const confirm = () => {
    toast({
      title: "Payment being processed",
      description: `Waiting to be added to the blockchain. Your ${plan.name} key & scripts will be emailed to ${email}.`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1a30] border border-yellow-400/30 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-pixel text-yellow-400 text-sm">
            <Zap className="w-4 h-4 fill-yellow-400" /> RIPS CHECKOUT
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-5 mt-2">
            <div>
              <Label className="text-slate-300">Select plan</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {plans.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPlan(p)}
                    className={`rounded-lg border p-3 text-center transition-colors ${
                      plan.id === p.id
                        ? "border-yellow-400 bg-yellow-400/10"
                        : "border-white/10 hover:border-yellow-400/40"
                    }`}
                  >
                    <div className="text-xs text-slate-400">{typeof p.days === "number" ? `${p.days} Days` : p.days}</div>
                    <div className="font-pixel text-yellow-400 text-base mt-1">${p.price}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-slate-300" htmlFor="email">Delivery email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#0a101e] border-white/10 focus-visible:ring-yellow-400 text-slate-100"
              />
              <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                <Mail className="w-3 h-3" /> Keys & scripts are sent here instantly after payment.
              </p>
            </div>

            <Button
              disabled={!validEmail}
              onClick={() => setStep(2)}
              className="w-full h-11 bg-yellow-400 hover:bg-yellow-300 text-[#0a101e] font-semibold rounded-lg disabled:opacity-50"
            >
              Continue to Payment
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 mt-2">
            <div className="flex items-center justify-between bg-[#0a101e] border border-white/10 rounded-lg px-4 py-3">
              <span className="text-slate-300">{plan.name}</span>
              <span className="font-pixel text-yellow-400">${plan.price}</span>
            </div>

            <div>
              <Label className="text-slate-300">Pay with</Label>
              {loadingWallets ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm mt-2 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading coins...
                </div>
              ) : wallets.length === 0 ? (
                <p className="text-slate-500 text-sm mt-2">No payment coins available right now.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {wallets.map((w) => (
                    <button
                      key={w.id || w.symbol}
                      onClick={() => setCoin(w)}
                      className={`rounded-lg border py-2 text-sm font-semibold transition-colors ${
                        coin && coin.symbol === w.symbol
                          ? "border-yellow-400 bg-yellow-400/10 text-yellow-400"
                          : "border-white/10 text-slate-300 hover:border-yellow-400/40"
                      }`}
                    >
                      {w.symbol}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {coin && (
              <div>
                <Label className="text-slate-300">{coin.coin} address</Label>
                <div className="mt-2 bg-[#0a101e] border border-white/10 rounded-lg px-3 py-2.5">
                  <span className="block text-xs sm:text-sm text-slate-200 font-mono break-all leading-relaxed select-all">
                    {coin.address}
                  </span>
                </div>
                <Button
                  onClick={copyAddr}
                  variant="outline"
                  className="w-full mt-2 h-10 bg-transparent border-yellow-400/50 hover:border-yellow-400 hover:bg-yellow-400/10 text-yellow-400 rounded-lg gap-2"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied!" : `Copy ${coin.symbol} Address`}
                </Button>
              </div>
            )}

            <p className="text-xs text-slate-500 flex items-start gap-1">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Send the exact amount. Payments are final and non-refundable. Verify the address before sending.
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 h-11 bg-transparent border-white/15 text-slate-200 hover:bg-white/5 rounded-lg"
              >
                Back
              </Button>
              <Button
                onClick={confirm}
                className="flex-1 h-11 bg-yellow-400 hover:bg-yellow-300 text-[#0a101e] font-semibold rounded-lg"
              >
                I've Paid
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseModal;
